import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DefaultSnackBarComponent } from '../default-snack-bar/default-snack-bar.component';
import { InitializerService } from '../../services/initializer.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentService } from '../../services/document.service';
import { CartService } from '../../services/cart.service';
import { MaskDirective } from 'ngx-mask';
import { AppDataService } from '../../services/app-data.service';
import { Booking } from 'projects/new-portal/src/@types/establishment-data';
import { ConfirmResponsibleDialogData } from './@types/confirm-responsible';

@Component({
    selector: 'my-portal-dialog-confirm-responsible',
    templateUrl: './dialog-confirm-responsible.component.html',
    styleUrls: ['./dialog-confirm-responsible.component.scss'],
})
export class DialogConfirmResponsibleComponent implements OnInit {
    public responsibleForm!: FormGroup;
    public readonly ng2TelInputOptions = {
        initialCountry: 'br',
        preferredCountries: ['BR', 'UY', 'AR', 'PY', 'BO', 'PE', 'CO', 'VE', 'GY', 'SR', 'GF'],
    };
    public phone: any = { sigla: 'br', ddi: '55' };
    public getScreenWidth: boolean = this.initializerService.isMobileDevice();

    private items: any;
    public cpfErrorMessage: string = '';

    public zipCodePattern: MaskDirective['patterns'] = {
        X: {
            pattern: /[a-zA-Z0-9\-\.]/
        }
    }
    public showResponsibleAddress: Booking['responsible_address'] = this.getResponsibleAddressVisibility();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ConfirmResponsibleDialogData,
        private dialogRef: MatDialogRef<DialogConfirmResponsibleComponent>,
        private formBuilder: FormBuilder,
        private initializerService: InitializerService,
        private snackbar: MatSnackBar,
        private documentService: DocumentService,
        private cartService: CartService,
        private appService: AppDataService,
    ) {}

    ngOnInit(): void {
        this.items = this.data.items;
        this.initResponsibleForm();
    }

    private initResponsibleForm(): void {
        const hotelKey = Object.keys(this.data.items.hotels)[0];
        const firstEstablishment = this.data.items.hotels[hotelKey];
        const responsible = this.data.responsibleData || firstEstablishment?.responsavel;

        this.responsibleForm = this.formBuilder.group({
            nome: new FormControl(null, [Validators.required]),
            sobrenome: new FormControl(null, [Validators.required]),
            data_nascimento: new FormControl(null),
            telefone: new FormControl(null, [Validators.required]),
            outro_contato: new FormControl(null),
            email: new FormControl(null, [Validators.required, Validators.email]),
            confirme_email: new FormControl(null),
        });

        if (this.showResponsibleAddress) {
            this.addResponsibleAddressControls();
        }

        this.responsibleForm.patchValue(responsible);
    }

    private getResponsibleAddressVisibility(): number {
        const currentData = this.initializerService.getCurrentDataAsValue();

        if (!currentData) return 0;

        const responsibleAddressConfig = currentData.config.booking.responsible_address;

        return responsibleAddressConfig;
    }


    private addResponsibleAddressControls(): void {
        this.responsibleForm.addControl('cep', new FormControl(null, [Validators.required]));
        this.responsibleForm.addControl('endereco', new FormControl({value: null, disabled: true}, [Validators.required]));
        this.responsibleForm.addControl('bairro', new FormControl({value: null, disabled: true}, [Validators.required]));
        this.responsibleForm.addControl('cidade', new FormControl({value: null, disabled: true}, [Validators.required]));
        this.responsibleForm.addControl('numero', new FormControl({value: null, disabled: true}));
        this.responsibleForm.addControl('complemento', new FormControl({value: 'outro', disabled: true}, [Validators.required]));
        this.responsibleForm.addControl('estado', new FormControl({value: null, disabled: true}, [Validators.required]));
        this.responsibleForm.addControl('pais', new FormControl({value: null, disabled: true}, [Validators.required]));
    }

    public onCepChange(): void {
        this.responsibleForm.enable();

        const cep: string | null = this.responsibleForm.controls.cep.value;

        if (cep?.length === 8) {
            this.appService.getDataCep(cep).subscribe((response) => {
                this.responsibleForm.patchValue({
                    endereco: response.street,
                    bairro: response.neighborhood,
                    cidade: response.city,
                    estado: response.state,
                    pais: 'Brasil'
                });
            });
        }
    }

    public onHandleSubmit(): void {
        if (this.responsibleForm.invalid) {
            this.openSnackBar('Preencha todos os campos obrigatórios', true);
            return;
        }

        const form = this.responsibleForm.value;

        for (const hotel of Object.keys(this.items.hotels)) {
            this.items.hotels[hotel].responsavel = {
                ...form,
                telefone: '+' + this.phone.ddi + form.telefone,
            };
        }

        this.updateCart();

        this.dialogRef.close(this.items);
    }

    public onCountryChange(country: any) {
        this.phone.sigla = country.iso2;
        this.phone.ddi = country.dialCode;

        const phoneNumber = this.responsibleForm.controls.telefone.value;

        this.responsibleForm.controls.telefone.patchValue(this.removeSpecificDDI(phoneNumber, this.phone.ddi));
    }

    private removeSpecificDDI(phoneNumber: string, ddi: string): string {
        const ddiPattern = new RegExp(`^\\s*${ddi}`);
        return phoneNumber.replace(ddiPattern, '');
    }

    public onTelInputMount(obj: any) {
        let number = '';

        if (this.responsibleForm.controls.telefone.value) {
            const phoneFormatted = this.responsibleForm.controls.telefone.value.replace(/[+\s]/g, '');
            obj.setNumber('+' + phoneFormatted);
            number = this.removeSpecificDDI(phoneFormatted, obj.s.dialCode);
        }

        if (number) {
            this.responsibleForm.controls.telefone.patchValue(number);
        } else {
            this.responsibleForm.controls.telefone.patchValue(null);
        }
    }

    public confirmDialog(): void {
        this.dialogRef.close('confirm');
    }

    private updateCart(): void {
        this.cartService.changeDataCartItem(this.items);
        this.documentService.getWindow()?.localStorage.setItem('cart', JSON.stringify(this.items));
        this.cartService.updateDataCart(this.items).subscribe();
    }

    private openSnackBar(msg: string, flag?: boolean | undefined): void {
        this.snackbar.openFromComponent(DefaultSnackBarComponent, {
            data: {
                message: msg,
            },
            horizontalPosition: 'end',
            verticalPosition: this.getScreenWidth ? 'top' : 'bottom',
            panelClass: flag ? 'error-snack-bar' : 'success-snack-bar',
            duration: 5000,
        });
    }
}
